/**
 * @fileoverview Edit-mode helpers for the Capabilities matrix.
 * @description Pure functions behind the page's Edit / Save flow: starting a draft from what each
 * role holds now, reading a cell through the draft, toggling one, listing what changed (the body of
 * Save), checking that a save is small enough for one request, and narrowing the module cards to the
 * permissions the Sales app uses. Kept free of React so the rules — Super Admin and inactive
 * permissions never change — are unit-testable.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { MAX_PERMISSIONS_PER_ROLE } from '@/features/capabilities/constants/capability-limits.constants'
import {
  compareGroupKeys,
  getPermissionControls,
} from '@/features/capabilities/utils/capability-label.utils'

import type {
  CapabilityChange,
  CapabilityDraft,
  CapabilityGroup,
  CapabilityMatrix,
  CapabilityPermission,
  CapabilityRole,
} from '@/features/capabilities/types/capabilities.types'

/**
 * @description The draft's key for one role/permission pair.
 * @param {string} roleCode - The role code.
 * @param {string} permissionCode - The permission code.
 * @returns {string} The key.
 */
export function capabilityKey(roleCode: string, permissionCode: string): string {
  return `${roleCode}|${permissionCode}`
}

/**
 * @description Whether a pair can be edited: not for a locked role, nor an inactive permission.
 * @param {CapabilityRole} role - The role.
 * @param {CapabilityPermission} permission - The permission.
 * @returns {boolean} True when the cell can be toggled.
 */
export function isEditable(role: CapabilityRole, permission: CapabilityPermission): boolean {
  return !role.locked && permission.active
}

/**
 * @description Starts an edit session: a copy of what every editable role holds right now.
 * @param {CapabilityMatrix} matrix - The matrix as loaded.
 * @returns {CapabilityDraft} The draft, with nothing changed yet.
 */
export function buildDraft(matrix: CapabilityMatrix): CapabilityDraft {
  const draft: CapabilityDraft = {}

  matrix.groups.forEach((group) =>
    group.permissions.forEach((permission) =>
      matrix.roles.forEach((role) => {
        if (isEditable(role, permission)) {
          draft[capabilityKey(role.code, permission.code)] = permission.grants[role.code] ?? false
        }
      }),
    ),
  )

  return draft
}

/**
 * @description Whether a role holds a permission, as the page should show it: through the draft
 * while editing, otherwise as loaded.
 * @param {CapabilityPermission} permission - The permission.
 * @param {string} roleCode - The role code.
 * @param {CapabilityDraft | null} draft - The edit draft, or null when not editing.
 * @returns {boolean} True when the role holds the permission.
 */
export function isGranted(
  permission: CapabilityPermission,
  roleCode: string,
  draft: CapabilityDraft | null,
): boolean {
  return draft?.[capabilityKey(roleCode, permission.code)] ?? permission.grants[roleCode] ?? false
}

/**
 * @description Flips one cell in the draft, from what the cell shows now: its draft value when it
 * has one, otherwise what was loaded. A cell with no draft entry (the matrix was refetched under
 * the draft, say) that is held therefore turns off, rather than being "flipped" from nothing to on.
 * @param {CapabilityDraft} draft - The current draft.
 * @param {CapabilityPermission} permission - The permission whose cell was clicked.
 * @param {string} roleCode - The role code.
 * @returns {CapabilityDraft} A new draft with that cell flipped.
 */
export function toggleDraft(
  draft: CapabilityDraft,
  permission: CapabilityPermission,
  roleCode: string,
): CapabilityDraft {
  return {
    ...draft,
    [capabilityKey(roleCode, permission.code)]: !isGranted(permission, roleCode, draft),
  }
}

/**
 * @description Finds a permission in the matrix by its code.
 * @param {CapabilityMatrix} matrix - The matrix as loaded.
 * @param {string} permissionCode - The permission code.
 * @returns {CapabilityPermission | undefined} The permission, or undefined when the matrix has none
 * with that code.
 */
export function findPermission(
  matrix: CapabilityMatrix,
  permissionCode: string,
): CapabilityPermission | undefined {
  return matrix.groups
    .flatMap((group) => group.permissions)
    .find((permission) => permission.code === permissionCode)
}

/**
 * @description Finds the role with the most changes when that is more than one save may carry. The
 * backend refuses a request with more than the limit for any one role, and a save is a single
 * all-or-nothing request, so the page warns instead of submitting (or splitting) it.
 * @param {CapabilityChange[]} changes - What a save would send.
 * @param {number} [limit] - The most permissions one role may change per save.
 * @returns {{ roleCode: string; count: number } | undefined} The role over the limit and how many
 * changes it has, or undefined when every role is within it.
 */
export function findRoleOverLimit(
  changes: CapabilityChange[],
  limit: number = MAX_PERMISSIONS_PER_ROLE,
): { roleCode: string; count: number } | undefined {
  const counts = new Map<string, number>()
  changes.forEach((change) => counts.set(change.roleCode, (counts.get(change.roleCode) ?? 0) + 1))

  return [...counts.entries()]
    .map(([roleCode, count]) => ({ roleCode, count }))
    .filter((entry) => entry.count > limit)
    .sort((left, right) => right.count - left.count)[0]
}

/**
 * @description The page's warning for a save too large for one request: which role, how many
 * changes, and the limit. Null when every role is within it.
 * @param {CapabilityChange[]} changes - What a save would send.
 * @param {CapabilityMatrix | undefined} matrix - The matrix the changes were made on.
 * @returns {{ roleName: string; count: number; max: number } | null} The warning, or null.
 */
export function buildLimitWarning(
  changes: CapabilityChange[],
  matrix: CapabilityMatrix | undefined,
): { roleName: string; count: number; max: number } | null {
  const overLimit = findRoleOverLimit(changes)
  if (!overLimit) {
    return null
  }

  return {
    roleName: matrix?.roles.find((role) => role.code === overLimit.roleCode)?.name ?? '',
    count: overLimit.count,
    max: MAX_PERMISSIONS_PER_ROLE,
  }
}

/**
 * @description Lists every cell that differs from what was loaded — what Save sends.
 * @param {CapabilityMatrix} matrix - The matrix as loaded.
 * @param {CapabilityDraft | null} draft - The edit draft, or null when not editing.
 * @returns {CapabilityChange[]} The changes, in table order.
 */
export function collectChanges(
  matrix: CapabilityMatrix,
  draft: CapabilityDraft | null,
): CapabilityChange[] {
  if (!draft) {
    return []
  }

  const changes: CapabilityChange[] = []

  matrix.groups.forEach((group) =>
    group.permissions.forEach((permission) =>
      matrix.roles.forEach((role) => {
        if (!isEditable(role, permission)) {
          return
        }

        const granted = isGranted(permission, role.code, draft)
        if (granted !== (permission.grants[role.code] ?? false)) {
          changes.push({
            roleCode: role.code,
            permissionCode: permission.code,
            granted,
            roleId: role.id,
            permissionId: permission.id,
            roleVersion: role.version,
          })
        }
      }),
    ),
  )

  return changes
}

/**
 * @description Whether the draft changes this one cell from what was loaded (for highlighting it).
 * @param {CapabilityPermission} permission - The permission.
 * @param {CapabilityRole} role - The role.
 * @param {CapabilityDraft | null} draft - The edit draft, or null when not editing.
 * @returns {boolean} True when the cell has an unsaved change.
 */
export function isChanged(
  permission: CapabilityPermission,
  role: CapabilityRole,
  draft: CapabilityDraft | null,
): boolean {
  return (
    draft !== null &&
    isEditable(role, permission) &&
    isGranted(permission, role.code, draft) !== (permission.grants[role.code] ?? false)
  )
}

/**
 * @description Orders the module cards (known groups in their set order, new ones after, by name)
 * and narrows them for the "Sales app" / "All permissions" filter: the Sales app view keeps only
 * permissions that control something in this app, and drops emptied cards.
 * @param {CapabilityGroup[]} groups - Every group.
 * @param {boolean} showAll - True to keep every permission.
 * @returns {CapabilityGroup[]} The groups to show.
 */
export function filterGroups(groups: CapabilityGroup[], showAll: boolean): CapabilityGroup[] {
  const ordered = [...groups].sort((left, right) => compareGroupKeys(left.key, right.key))

  if (showAll) {
    return ordered
  }

  return ordered
    .map((group) => ({
      ...group,
      permissions: group.permissions.filter(
        (permission) => getPermissionControls(permission.code).length > 0,
      ),
    }))
    .filter((group) => group.permissions.length > 0)
}

/** An edit session after a refused save: the matrix and draft, with only the stale roles redone. */
export interface RebasedSession {
  base: CapabilityMatrix
  draft: CapabilityDraft
  /** The names of the roles whose edits were discarded, for the message shown to the admin. */
  staleRoleNames: string[]
}

/**
 * @description Finds the roles someone else changed since editing started: those whose row version
 * now differs from the one the session began with. A refused save names no role, so this is how the
 * stale ones are told apart from the rest. When none differs (the refetch after the refusal failed,
 * say) the set is empty: nothing says which role conflicted, so no edit is discarded and none is
 * blamed — the page tells the admin to cancel and reload instead.
 * @param {CapabilityMatrix} base - The matrix the session started from.
 * @param {CapabilityMatrix} live - The matrix as refetched after the refusal.
 * @returns {Set<string>} The codes of the stale roles.
 */
export function findStaleRoleCodes(base: CapabilityMatrix, live: CapabilityMatrix): Set<string> {
  const liveVersions = new Map(live.roles.map((role) => [role.code, role.version]))

  return new Set(
    base.roles
      .filter((role) => liveVersions.has(role.code) && liveVersions.get(role.code) !== role.version)
      .map((role) => role.code),
  )
}

/**
 * @description Takes the stale roles' columns (their row version, lock and grants) from the live
 * matrix and leaves every other role as the session saw it.
 * @param {CapabilityMatrix} base - The matrix the session started from.
 * @param {CapabilityMatrix} live - The matrix as refetched.
 * @param {Set<string>} stale - The codes of the roles to take from the live matrix.
 * @returns {CapabilityMatrix} The matrix with only the stale roles brought up to date.
 */
function rebaseMatrix(
  base: CapabilityMatrix,
  live: CapabilityMatrix,
  stale: Set<string>,
): CapabilityMatrix {
  const liveRoles = new Map(live.roles.map((role) => [role.code, role]))
  const livePermissions = new Map(
    live.groups
      .flatMap((group) => group.permissions)
      .map((permission) => [permission.code, permission]),
  )

  return {
    ...base,
    roles: base.roles.map((role) => (stale.has(role.code) && liveRoles.get(role.code)) || role),
    groups: base.groups.map((group) => ({
      ...group,
      permissions: group.permissions.map((permission) => {
        const fresh = livePermissions.get(permission.code)
        if (!fresh) {
          return permission
        }

        const grants = { ...permission.grants }
        stale.forEach((code) => {
          grants[code] = fresh.grants[code] ?? false
        })
        return { ...permission, grants }
      }),
    })),
  }
}

/**
 * @description Re-bases an edit session after a save was refused as stale (412): the roles someone
 * else changed lose their edits and take the live grants and row version, while every other role
 * keeps its edits and the version it started from — a refused save changes nothing, so those are
 * still current. The admin therefore redoes only what actually conflicted.
 * @param {CapabilityMatrix} base - The matrix the session started from.
 * @param {CapabilityDraft} draft - The session's draft.
 * @param {CapabilityMatrix} live - The matrix as refetched after the refusal.
 * @returns {RebasedSession} The new base and draft, and the names of the roles that were re-based.
 */
export function rebaseStaleRoles(
  base: CapabilityMatrix,
  draft: CapabilityDraft,
  live: CapabilityMatrix,
): RebasedSession {
  const stale = findStaleRoleCodes(base, live)
  const nextBase = rebaseMatrix(base, live, stale)
  const nextDraft = buildDraft(nextBase)

  nextBase.groups.forEach((group) =>
    group.permissions.forEach((permission) =>
      nextBase.roles
        .filter((role) => !stale.has(role.code))
        .forEach((role) => {
          const key = capabilityKey(role.code, permission.code)
          if (key in nextDraft && key in draft) {
            nextDraft[key] = draft[key] ?? false
          }
        }),
    ),
  )

  return {
    base: nextBase,
    draft: nextDraft,
    staleRoleNames: nextBase.roles.filter((role) => stale.has(role.code)).map((role) => role.name),
  }
}
